import { useMemo, useState } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import Button, { TooltipButton } from "@/v2-backoffice-client/components/button"
import Table, {
  GROUPING_RANGE_DATES,
  createColumns,
  summaryFraction,
  useTableQuery,
  type TableProps,
  type TableQueryParams,
} from "@/v2-backoffice-client/components/table"
import { useCustomerQuery } from "@/v2-backoffice-client/customer/customer.query"
import { useCustomerOwnersQuery } from "@/v2-backoffice-client/customer/customer.owners.query"
import type { CustomerFragment } from "@/v2-backoffice-client/customer/__types__/customer.fragment"
import {
  FENERUM_ACCOUNT_BASE_URL,
  HUBSPOT_BASE_URL,
} from "@/v2-backoffice-client/constants"
import TruncatedList from "@/v2-backoffice-client/components/truncatedList"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"
import MigrationProgress from "@/v2-backoffice-client/tenant/migration/MigrationProgress"
import CustomerMigrateManyButton from "@/v2-backoffice-client/customer/migration/CustomerMigrateManyButton"
import { useSearchParams } from "@/v2-backoffice-client/utils"
import { buildScopeUsageColumns } from "@/v2-backoffice-client/usage/usage.columns"
import LicenseGoverningBadge from "@/v2-backoffice-client/license/LicenseGoverningBadge"
import { useLicenseStatusesQuery } from "@/v2-backoffice-client/license/license.statuses.query"
import {
  getLicenseGoverningStatusLabel,
  getLicenseStatusFilterVisual,
} from "@/v2-backoffice-client/license/license.utils"
import {
  LICENSE_STATUS_ACTIVE,
  LICENSE_STATUS_NONE,
} from "@/v2-backoffice-client/license/license.constants"
import CustomerLicenseBadge from "@/v2-backoffice-client/customer/license/CustomerLicenseBadge"
import {
  CUSTOMER_SEAT_STATUSES,
  deriveCustomerSeatFacts,
  describeCustomerSeatFacts,
} from "@/v2-backoffice-client/customer/license/customer.license.utils"
import CustomerActionMenu from "@/v2-backoffice-client/customer/CustomerActionMenu"
import CustomerCompanyBadge from "@/v2-backoffice-client/customer/company/CustomerCompanyBadge"
import CustomerCompanyLinkDialog from "@/v2-backoffice-client/customer/company/CustomerCompanyLinkDialog"
import {
  deriveCompanyLinkStatus,
  getCompanyLinkStatusLabel,
  COMPANY_LINK_STATUS_NOT_LINKED,
} from "@/v2-backoffice-client/customer/company/customerCompany.constants"
import CustomerBillingBadge from "@/v2-backoffice-client/customer/billing/CustomerBillingBadge"
import CustomerPaymentBadge from "@/v2-backoffice-client/customer/billing/CustomerPaymentBadge"
import CustomerValueBadge from "@/v2-backoffice-client/customer/billing/CustomerValueBadge"
import { buildCustomerViewLink } from "@/v2-backoffice-client/customer/customer.links"
import {
  BILLING_ACCOUNT_STATUS_ARCHIVED,
  BILLING_ACCOUNT_STATUS_LINKABLE,
  BILLING_ACCOUNT_STATUS_LINKED,
  BILLING_ACCOUNT_STATUS_NOT_LINKED,
  PAYMENT_HEALTH_CURRENT,
  PAYMENT_HEALTH_NONPAYMENT,
  PAYMENT_HEALTH_OVERDUE,
  PAYMENT_HEALTH_SETTLED,
  VALUE_TIER_DORMANT,
  VALUE_TIER_HIGH,
  VALUE_TIER_LOW,
  VALUE_TIER_MID,
  VALUE_TIER_VIP,
  getBillingAccountStatusLabel,
  getPaymentHealthLabel,
  getValueTierLabel,
} from "@/v2-backoffice-client/customer/billing/customerBilling.constants"
import {
  formatCompactAmount,
  formatInvoiceAmount,
} from "@/v2-backoffice-client/customer/billing/customerBilling.utils"

const PAGE_SIZE = 100
const COLUMN_SIZING = {
  ExternalCustomerId: 150,
  TenantNames: 150,
  OwnerName: 150,
  LicenseStatusCode: 150,
  LicensePlanNames: 150,
  LicenseSeats: 110,
  Billed12MPerUser: 120,
  UserCount: 70,
  ActiveUserCount: 80,
  InActiveUserCount: 80,
}

// The status pills' filter target: the customer's governing license status
// from lic.Licenses (licenseFacts in backoffice.Get_Acc_Customers), 0 = no
// license held. This replaced the claim-era HasLicenses bucket (a positive
// UserLicenses registered in HubSpot) when the License domain took over as
// the license truth — and it absorbed the standalone licenses board: the
// pills + Trial Ends sort ARE the trials worklist, scoped to customers.
const LICENSE_STATUS_COLUMN = "LicenseStatusCode"

/**
 * The status pill's server filter for the table's FIRST query, resolved
 * from the same URL param Table.StatusFilter persists to — in exactly the
 * shape buildQueryParams emits once the pill mounts, so Apollo sees
 * deep-equal variables and never fires a query the pill immediately
 * replaces. No param means the default Active pill.
 */
function buildLicenseStatusParams(
  statusParam: string | null,
): TableQueryParams {
  // The Over seats pill persists its filter value, not a status code, and
  // filters the seat column instead of the status column.
  if (statusParam === CUSTOMER_SEAT_STATUSES.OVER_LIMIT) {
    return {
      filters: [
        { column: "LicenseSeats", eq: CUSTOMER_SEAT_STATUSES.OVER_LIMIT },
      ],
    }
  }
  if (statusParam !== null && /^\d+$/.test(statusParam)) {
    return { filters: [{ column: LICENSE_STATUS_COLUMN, eq: statusParam }] }
  }
  return {
    filters: [
      { column: LICENSE_STATUS_COLUMN, eq: String(LICENSE_STATUS_ACTIVE) },
    ],
  }
}

type BuildColumnsInput = {
  onLinkCompany: (customer: CustomerFragment) => void
  /** License-status options (statuses + "No license") for the License filter. */
  licenseStatusOptions: { label: string; value: string }[]
  /** Status display names from the getLicenseStatuses lookup. */
  licenseStatusNamesByCode: Map<number, string>
  /**
   * Company-owner options from the getCustomerOwners lookup (the whole
   * book's distinct owners, not just the loaded page's).
   */
  ownerOptions: { label: string; value: string }[]
}

// buildColumns is a function (not a module constant) because the company
// badge cell needs the "open the link dialog for this row" callback, which
// is table-level state, and the license cells need the status lookup.
function buildColumns(input: BuildColumnsInput) {
  const {
    onLinkCompany,
    licenseStatusOptions,
    licenseStatusNamesByCode,
    ownerOptions,
  } = input
  return createColumns<CustomerFragment>({
    enableSelection: true,
    enableSorting: true,
    enableHiding: true,
    columns: {
      Name: {
        id: "Name",
        label: "Name",
        filter: { type: "text", placeholder: "Name..." },
        // App-created customers have no HubSpot id yet, and the customer
        // panel is still keyed by it — no link until they are linked.
        to: ({ row }) =>
          row.original.ExternalCustomerId
            ? `?v=customer:${row.original.ExternalCustomerId}`
            : undefined,
      },
      ExternalCustomerId: {
        id: "ExternalCustomerId",
        label: "Hubspot Id",
        defaultHidden: true,
        filter: { type: "text", placeholder: "HubSpot id..." },
        to: ({ row }) =>
          row.original.ExternalCustomerId
            ? `${HUBSPOT_BASE_URL}/${row.original.ExternalCustomerId}`
            : undefined,
      },
      // The Fenerum sibling of Hubspot Id: the linked billing account's
      // uuid, linking to its Fenerum page. Empty until the customer has a
      // linked account (the Billing Account column tells that story).
      BillingAccountUuid: {
        id: "BillingAccountUuid",
        label: "Fenerum Id",
        defaultHidden: true,
        enableSorting: false,
        to: ({ row }) =>
          row.original.BillingAccountUuid
            ? `${FENERUM_ACCOUNT_BASE_URL}/${row.original.BillingAccountUuid}/`
            : undefined,
      },
      // How the customer relates to its HubSpot company (the customer
      // sibling of the users board's contact badge). Client-derived from
      // the Company link row — not server-sortable.
      CompanyLink: {
        id: "CompanyLink",
        label: "HubSpot Link",
        enableSorting: false,
        enableGrouping: true,
        getValue: (row) =>
          getCompanyLinkStatusLabel(deriveCompanyLinkStatus(row)),
        renderCell({ row }) {
          const isUnlinked =
            deriveCompanyLinkStatus(row.original) ===
            COMPANY_LINK_STATUS_NOT_LINKED
          return (
            <CustomerCompanyBadge
              customer={row.original}
              showHealthy
              action={
                isUnlinked ? (
                  <Button
                    size="tiny"
                    variant="outline"
                    onClick={() => {
                      onLinkCompany(row.original)
                    }}
                  >
                    Link HubSpot company
                  </Button>
                ) : undefined
              }
            />
          )
        },
        exportValue({ row }) {
          return getCompanyLinkStatusLabel(
            deriveCompanyLinkStatus(row.original),
          )
        },
      },
      // How the customer relates to its Fenerum billing account — the
      // billing sibling of the HubSpot Link column. Server-derived
      // (billingFacts in Get_Acc_Customers), so the filter runs server-side
      // against the same CASE the badge renders.
      BillingAccountStatus: {
        id: "BillingAccountStatus",
        label: "Billing Account",
        enableSorting: false,
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any billing link",
          options: [
            {
              label: "Linked",
              value: BILLING_ACCOUNT_STATUS_LINKED,
              dotVariant: "success-1",
            },
            {
              label: "Linkable",
              value: BILLING_ACCOUNT_STATUS_LINKABLE,
              dotVariant: "warning",
            },
            { label: "Not linked", value: BILLING_ACCOUNT_STATUS_NOT_LINKED },
            { label: "Archived", value: BILLING_ACCOUNT_STATUS_ARCHIVED },
          ],
        },
        getValue: (row) =>
          getBillingAccountStatusLabel(row.BillingAccountStatus),
        renderCell({ row }) {
          return <CustomerBillingBadge customer={row.original} />
        },
        exportValue({ row }) {
          return getBillingAccountStatusLabel(row.original.BillingAccountStatus)
        },
      },
      // Where the customer's 12-month billed revenue ranks among all
      // actively billed customers — server-derived (lic.CustomerRevenueFacts
      // via Get_Acc_Customers), so the filter runs server-side against the
      // same CASE the badge renders. Empty for customers with no invoices.
      ValueTier: {
        id: "ValueTier",
        label: "Value",
        enableSorting: false,
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any value",
          options: [
            { label: "VIP", value: VALUE_TIER_VIP, dotVariant: "primary" },
            { label: "High", value: VALUE_TIER_HIGH, dotVariant: "success-1" },
            { label: "Mid", value: VALUE_TIER_MID },
            { label: "Low", value: VALUE_TIER_LOW },
            {
              label: "Dormant",
              value: VALUE_TIER_DORMANT,
              dotVariant: "warning",
            },
          ],
        },
        getValue: (row) => getValueTierLabel(row.ValueTier),
        renderCell({ row }) {
          return <CustomerValueBadge customer={row.original} />
        },
        exportValue({ row }) {
          return getValueTierLabel(row.original.ValueTier)
        },
      },
      // The 12-month book behind the value band — the number sales sorts on to
      // walk the customers by value (server-side sort via the revenueFacts
      // join in Get_Acc_Customers).
      Billed12M: {
        id: "Billed12M",
        label: "Billed (12m)",
        filter: { type: "number-range" },
        getValue: (row) => row.Billed12M ?? null,
        renderCell({ row }) {
          if (row.original.Billed12M == null) return null
          return (
            <span className="tabular-nums">
              {formatInvoiceAmount(
                row.original.Billed12M,
                row.original.PrimaryCurrency,
              )}
            </span>
          )
        },
        exportValue({ row }) {
          return row.original.Billed12M ?? ""
        },
      },
      // What each user effectively costs the customer today — the number
      // behind "are similar customers paying similar per-user prices?".
      // Client-derived from two server columns, so no server sort; the license
      // panel's Customers tab carries the MRR-based per-seat/per-user twins.
      Billed12MPerUser: {
        id: "Billed12MPerUser",
        label: "Per User (12m)",
        description:
          "Billed the last 12 months divided by total users — what each user effectively costs the customer.",
        enableSorting: false,
        getValue: (row) =>
          row.Billed12M != null && row.UserCount > 0
            ? Math.round(row.Billed12M / row.UserCount)
            : null,
        renderCell({ row }) {
          const { Billed12M, UserCount, PrimaryCurrency } = row.original
          if (Billed12M == null || UserCount <= 0) return null
          return (
            <span className="tabular-nums">
              {formatInvoiceAmount(
                Math.round(Billed12M / UserCount),
                PrimaryCurrency,
              )}
            </span>
          )
        },
        exportValue({ row }) {
          const { Billed12M, UserCount } = row.original
          return Billed12M != null && UserCount > 0
            ? Math.round(Billed12M / UserCount)
            : ""
        },
      },
      // Whether the customer is behind on billing — worst invoice state
      // governs (Nonpayment > Overdue > Current > Settled), server-derived
      // like ValueTier. Empty for customers with no invoices.
      PaymentHealth: {
        id: "PaymentHealth",
        label: "Payment",
        enableSorting: false,
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any payment health",
          // Labels + dots mirror the invoice-status pills (Fenerum's own
          // vocabulary): Open = outstanding within terms, Paid = nothing
          // outstanding.
          options: [
            {
              label: "Non-payment",
              value: PAYMENT_HEALTH_NONPAYMENT,
              dotVariant: "orange",
            },
            {
              label: "Overdue",
              value: PAYMENT_HEALTH_OVERDUE,
              dotVariant: "destructive",
            },
            {
              label: "Open",
              value: PAYMENT_HEALTH_CURRENT,
              dotVariant: "info",
            },
            {
              label: "Paid",
              value: PAYMENT_HEALTH_SETTLED,
              dotVariant: "success-1",
            },
          ],
        },
        getValue: (row) => getPaymentHealthLabel(row.PaymentHealth),
        renderCell({ row }) {
          return <CustomerPaymentBadge customer={row.original} />
        },
        exportValue({ row }) {
          return getPaymentHealthLabel(row.original.PaymentHealth)
        },
      },
      // Occupied-vs-licensed seat pressure. Filtering runs server-side (the
      // CustomerSeatStatus wire literals compile to a SQL CASE in
      // customersFilterRegistry); sorting runs server-side on the seat GAP
      // (occupied - cap), so descending puts the biggest overruns first.
      // Over limit is amber everywhere — a billing conversation, not
      // breakage (LicenseSeatBadge owns that rule).
      LicenseSeats: {
        id: "LicenseSeats",
        label: "License Seats",
        getValue: (row) => row.UserCount + (row.PendingInvitationCount ?? 0),
        filter: {
          type: "multi-select",
          placeholder: "Any seat status",
          toolbar: true,
          options: [
            {
              label: "Over limit",
              value: CUSTOMER_SEAT_STATUSES.OVER_LIMIT,
              dotVariant: "warning",
            },
            {
              label: "Within limit",
              value: CUSTOMER_SEAT_STATUSES.OK,
              dotVariant: "success-1",
            },
            {
              label: "Uncapped",
              value: CUSTOMER_SEAT_STATUSES.UNCAPPED,
            },
          ],
        },
        renderCell({ row }) {
          return <CustomerLicenseBadge customer={row.original} />
        },
        exportValue({ row }) {
          return describeCustomerSeatFacts(
            deriveCustomerSeatFacts(row.original),
          )
        },
        summaryValue({ row }) {
          const facts = deriveCustomerSeatFacts(row.original)
          return summaryFraction(facts.seatsUsed, facts.licensedSeats)
        },
      },
      // The governing license status from lic.Licenses — what the status
      // pills filter. Filtering and the status-rank sort run server-side
      // against the licenseFacts APPLY in Get_Acc_Customers
      // (customersFilterRegistry), so they match the badge.
      LicenseStatusCode: {
        id: LICENSE_STATUS_COLUMN,
        label: "License status",
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any license status",
          options: licenseStatusOptions,
          operators: ["EQ", "NEQ", "IN"],
        },
        getValue: (row) =>
          getLicenseGoverningStatusLabel(
            row.LicenseStatusCode,
            licenseStatusNamesByCode,
          ),
        renderCell({ row }) {
          return (
            <LicenseGoverningBadge
              license={row.original}
              statusName={
                row.original.LicenseStatusCode != null
                  ? licenseStatusNamesByCode.get(row.original.LicenseStatusCode)
                  : undefined
              }
            />
          )
        },
        exportValue({ row }) {
          return getLicenseGoverningStatusLabel(
            row.original.LicenseStatusCode,
            licenseStatusNamesByCode,
          )
        },
      },
      LicensePlanNames: {
        id: "LicensePlanNames",
        label: "Licenses",
        enableGrouping: true,
        // Aggregated multi-license string: no server sort expression — same
        // no-op-sort reasoning as TenantNames. The text filter matches any
        // single held license/product name server-side (customersFilterRegistry).
        enableSorting: false,
        filter: {
          type: "text",
          placeholder: "License or product...",
          operators: ["CONTAINS", "EQ"],
        },
        getValue: (row) => row.LicensePlanNames ?? "",
        // Each held license links into its license panel; license-less grants (a
        // product with no PlanId) render as plain text.
        renderCell({ row }) {
          const refs = row.original.LicensePlans ?? []
          if (refs.length === 0) return null
          return (
            <TruncatedList
              items={refs.map((ref) => ({
                label: ref.Name,
                to: ref.PlanId != null ? `?v=plan:${ref.PlanId}` : undefined,
              }))}
            />
          )
        },
        exportValue({ row }) {
          return row.original.LicensePlanNames ?? ""
        },
      },
      Stage: {
        id: "Stage",
        label: "Stage",
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any stage",
          options: [
            { label: "Lead", value: "Lead" },
            { label: "Opportunity", value: "Opportunity" },
            { label: "Customer", value: "customer" },
            { label: "Subscriber", value: "Subscriber" },
            { label: "Other", value: "other" },
          ],
        },
      },
      // The HubSpot company owner — the CRM book each CSM/sales person
      // works. Filter and sort run server-side against the
      // acc.CustomerCompanies snapshot (customersFilterRegistry); grouping
      // by owner IS the per-CSM worklist the weekly trial report used to
      // hand-build.
      OwnerName: {
        id: "OwnerName",
        label: "Company Owner",
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any owner",
          options: ownerOptions,
        },
        getValue: (row) => row.OwnerName ?? "",
      },
      TenantNames: {
        id: "TenantNames",
        label: "Team(s)",
        enableGrouping: true,
        // Server-side: EXISTS over acc.Tenants (customersFilterRegistry) —
        // a match on any single team hits.
        filter: {
          type: "text",
          placeholder: "Team name...",
          operators: ["CONTAINS", "EQ"],
        },
        getValue: (row) => row.TenantNames?.[0] ?? "",
        renderCell({ row }) {
          return (
            <TruncatedList
              items={row.original.Tenants.map((tenant) => ({
                label: tenant.Name,
                to: `?v=tenant:${tenant.Id}`,
              }))}
            />
          )
        },
        exportValue({ row }) {
          return row.original.TenantNames?.join(", ") ?? ""
        },
      },
      // The trial clock behind the License badge's countdown, as a date
      // column of its own so sales can filter "trials ending this week".
      LicenseTrialEndsAt: {
        id: "LicenseTrialEndsAt",
        label: "Trial Ends",
        defaultSize: 110,
        defaultHidden: true,
        filter: { type: "date-filter" },
        groupingRanges: GROUPING_RANGE_DATES,
        renderCell({ row }) {
          const value = row.original.LicenseTrialEndsAt
          return value ? formatDateString(value) : null
        },
        exportValue({ row }) {
          return formatDateString(row.original.LicenseTrialEndsAt)
        },
      },
      // Next renewal among licenses that will bill again — the worklist
      // date for "renewals due this quarter" and the renewal insight.
      LicenseRenewsAt: {
        id: "LicenseRenewsAt",
        label: "Renews",
        defaultSize: 110,
        defaultHidden: true,
        filter: { type: "date-filter" },
        groupingRanges: GROUPING_RANGE_DATES,
        renderCell({ row }) {
          const value = row.original.LicenseRenewsAt
          return value ? formatDateString(value) : null
        },
        exportValue({ row }) {
          return formatDateString(row.original.LicenseRenewsAt)
        },
      },
      UserCount: {
        id: "UserCount",
        label: "Total Users",
        filter: { type: "number-range" },
      },
      // The migration target group (FUS-927): users with v2 evidence.
      // The wave selection filter: V2 Users > 0 → select all → Fusion.
      MigrationV2Users: {
        id: "MigrationV2Users",
        label: "V2 Users",
        defaultSize: 90,
        enableSorting: false,
        filter: { type: "number-range" },
      },
      // Fusion-login migration progress across every user of the customer
      // (FUS-927). The id doubles as the server sort key (activated/total).
      MigrationProgress: {
        id: "MigrationProgress",
        label: "Login Migration",
        defaultSize: 160,
        renderCell({ row }) {
          return <MigrationProgress rollup={row.original} />
        },
        exportValue({ row }) {
          return `${row.original.MigrationActivated ?? 0}/${row.original.MigrationTotal ?? 0}`
        },
        summaryValue({ row }) {
          return summaryFraction(
            row.original.MigrationActivated,
            row.original.MigrationTotal,
          )
        },
      },
      ActiveUserCount: {
        id: "ActiveUserCount",
        label: "Active Users",
        filter: { type: "number-range" },
      },
      InactiveUserCount: {
        id: "InactiveUserCount",
        label: "Inactive Users",
        filter: { type: "number-range" },
      },
      // Deep link into the customer panel's Activity tab so staff can dig
      // into the users behind the aggregates.
      ...buildScopeUsageColumns<CustomerFragment>({
        buildTo: (row) =>
          row.ExternalCustomerId
            ? buildCustomerViewLink(row.ExternalCustomerId, "sign-ins")
            : undefined,
        // At-risk churn badges carry the money behind the warning.
        getAtStake: (row) =>
          row.Billed12M != null && row.Billed12M > 0
            ? `${formatCompactAmount(row.Billed12M, row.PrimaryCurrency)} billed the last 12 months rides on this account.`
            : null,
      }),
    },
  })
}

export type CustomerTableProps = Omit<
  TableProps<CustomerFragment>,
  "data" | "columns"
> & {
  isLoading?: boolean
  /**
   * Customers per governing license status (getCustomerCounts
   * byLicenseStatus), keyed by status code with 0 = no license. Feeds the
   * pill counts; All is the sum.
   */
  statusCounts?: Map<number, number>
  /** Customers over their seat cap (getCustomerCounts overSeatCap), the Over seats pill's count. */
  overSeatCapCount?: number
}

// The Over seats pill's URL value: outside the lic.LicenseStatuses codes
// (1–6) and the no-license sentinel (0), so a deep link can never collide
// with a status pill. It filters the License Seats column, not the status
// column (filterColumn), the way the Users board's Flagged pill does.
const OVER_SEAT_CAP_PILL_VALUE = 99

function CustomerTable(props: CustomerTableProps) {
  const {
    className,
    isLoading,
    statusCounts,
    overSeatCapCount,
    ...tableProps
  } = props

  const opener = useAppViewSheetOpener()
  const [searchParams] = useSearchParams()

  // The customer whose HubSpot link dialog is open (from a badge cell's
  // "Link HubSpot company" action) — the only link surface for app-created
  // customers, whose panel isn't reachable until they have a HubSpot id.
  const [linkTarget, setLinkTarget] = useState<CustomerFragment | null>(null)

  // License statuses for the pills, the License filter options and the
  // badge names. "No license" is appended as the sentinel the server emits
  // for customers that hold none.
  const { data: licenseStatusData } = useLicenseStatusesQuery()
  const licenseStatuses = licenseStatusData?.getLicenseStatuses
  const licenseStatusNamesByCode = useMemo(
    () =>
      new Map(
        (licenseStatuses ?? []).map((status) => [status.Code, status.Name]),
      ),
    [licenseStatuses],
  )
  const licenseStatusOptions = useMemo(
    () => [
      ...(licenseStatuses ?? []).map((status) => ({
        label: status.Name,
        value: String(status.Code),
      })),
      { label: "No license", value: String(LICENSE_STATUS_NONE) },
    ],
    [licenseStatuses],
  )

  // The whole book's distinct company owners, so the facet's options don't
  // shrink to whatever owners happen to be on the loaded page.
  const { data: ownersData } = useCustomerOwnersQuery()
  const ownerOptions = useMemo(
    () =>
      (ownersData?.getCustomerOwners ?? []).map((owner) => ({
        label: owner,
        value: owner,
      })),
    [ownersData],
  )

  const columns = useMemo(
    () =>
      buildColumns({
        onLinkCompany: setLinkTarget,
        licenseStatusOptions,
        licenseStatusNamesByCode,
        ownerOptions,
      }),
    [licenseStatusOptions, licenseStatusNamesByCode, ownerOptions],
  )

  const totalCount = useMemo(() => {
    if (!statusCounts) return undefined
    let total = 0
    for (const count of statusCounts.values()) total += count
    return total
  }, [statusCounts])

  const {
    items,
    pageInfo,
    loading,
    error,
    refetch,
    onFetchMore,
    onQueryChange,
  } = useTableQuery({
    pageSize: PAGE_SIZE,
    useQuery: useCustomerQuery,
    select: (data) => data.getCustomers,
    initialParams: buildLicenseStatusParams(searchParams.get("status")),
  })

  return (
    <Table
      className={cn("customer-table", className)}
      tableId="customer-table"
      data={items}
      columns={columns}
      initialColumnSizing={COLUMN_SIZING}
      enableGrouping
      enableRowSelection
      manualSorting
      manualFiltering
      onQueryChange={onQueryChange}
      getHighlightedRowId={(row) => {
        return opener.getIsActive(
          "customer",
          row.original.ExternalCustomerId ?? "",
        )
      }}
      {...tableProps}
    >
      <Table.Toolbar>
        <Table.StatusFilter
          column={LICENSE_STATUS_COLUMN}
          defaultValue={LICENSE_STATUS_ACTIVE}
          isLoading={isLoading}
        >
          <Table.StatusFilter.Option value={null} count={totalCount}>
            All
          </Table.StatusFilter.Option>

          {/* Each tab wears the status's own icon and hue, the same as the
              pill on the customer's row and in her panel, so the thing you
              filter by and the thing you see are recognisably one. */}
          {(licenseStatuses ?? []).map((status) => {
            const visual = getLicenseStatusFilterVisual(status.Code)
            return (
              <Table.StatusFilter.Option
                key={status.Code}
                value={status.Code}
                count={statusCounts?.get(status.Code) ?? 0}
                iconSrc={visual.iconSrc}
                variant={visual.variant}
                title={status.Description}
              >
                {status.Name}
              </Table.StatusFilter.Option>
            )
          })}

          <Table.StatusFilter.Option
            value={LICENSE_STATUS_NONE}
            count={statusCounts?.get(LICENSE_STATUS_NONE)}
            iconSrc={getLicenseStatusFilterVisual(LICENSE_STATUS_NONE).iconSrc}
          >
            No license
          </Table.StatusFilter.Option>

          {/* Seats are reported, never enforced (invitations go through;
              the customer is told seats beyond the license may be
              invoiced), so the overrun is a sales worklist: this pill is
              where it is read, and the customer panel's Licensing tab is
              where it is closed. */}
          <Table.StatusFilter.Option
            value={OVER_SEAT_CAP_PILL_VALUE}
            filterColumn="LicenseSeats"
            filterValue={CUSTOMER_SEAT_STATUSES.OVER_LIMIT}
            variant="warning"
            iconSrc="triangle-warning"
            count={overSeatCapCount}
            title="Customers with more seats in use (users plus open invitations) than their live licenses allow. Nothing is blocked; the sales owner agrees the seat count with the customer and sets it on the customer panel's Licensing tab."
          >
            Over seats
          </Table.StatusFilter.Option>
        </Table.StatusFilter>

        <div className="flex-1" />
        <Table.Search column="Name" placeholder="Search by name..." />
        <Table.ToolbarFilters />

        <Separator orientation="vertical" />
        <Table.ToolbarMenu onRefresh={refetch} exportFileName="customers" />
      </Table.Toolbar>

      <Table.FilterList />
      <Table.GroupList />

      <Table.RowToolbar<CustomerFragment> column="Name">
        {(customer) => <CustomerActionMenu customer={customer} />}
      </Table.RowToolbar>

      <Table.Content
        countLabel="customers"
        isLoading={isLoading || loading}
        error={error}
        pageInfo={pageInfo}
        onFetchMore={onFetchMore}
      />

      <Table.SelectionToolbar<CustomerFragment>>
        {(customers, reset) => (
          <>
            <Table.ExportSelectionButton
              fileName="customers"
              selectionCount={customers.length}
            />
            <CustomerMigrateManyButton
              customers={customers}
              onCompleted={reset}
            />
            <TooltipButton
              variant="ghost"
              isStacked
              iconSrc="hubspot"
              content="Open in HubSpot"
              onClick={() => {
                for (const customer of customers) {
                  if (!customer.ExternalCustomerId) continue
                  window.open(
                    `${HUBSPOT_BASE_URL}/${customer.ExternalCustomerId}`,
                    "_blank",
                  )
                }
                reset()
              }}
            >
              HubSpot
            </TooltipButton>
            <TooltipButton
              variant="ghost"
              isStacked
              iconSrc="bank"
              content="Open the linked billing accounts in Fenerum (skips customers without one)"
              onClick={() => {
                for (const customer of customers) {
                  if (!customer.BillingAccountUuid) continue
                  window.open(
                    `${FENERUM_ACCOUNT_BASE_URL}/${customer.BillingAccountUuid}/`,
                    "_blank",
                  )
                }
                reset()
              }}
            >
              Fenerum
            </TooltipButton>
          </>
        )}
      </Table.SelectionToolbar>

      <CustomerCompanyLinkDialog
        customer={linkTarget}
        open={!!linkTarget}
        onOpenChange={(open) => {
          if (!open) setLinkTarget(null)
        }}
      />
    </Table>
  )
}

export default CustomerTable
